import api from '../../../services/api';

/**
 * Profile endpoints.
 *
 * Who may view whom (enforced by the backend): an admin any user, a team lead themselves and
 * their direct reports, an employee only themselves.
 */

/**
 * GET /rfp/auth/users/{user_id}
 * 200: { email, first_name, last_name, designation, department, location, phone_number, _id,
 *        role, status, password_set, employee_name, assigned, manager_id, manager_name, avatar_url }
 * 403: not allowed to view this user.  422: validation error.
 */
export const fetchUserProfile = async (userId) => {
    return api.get(`/rfp/auth/users/${encodeURIComponent(userId)}`);
};

/**
 * PATCH /rfp/auth/users/{user_id}  { first_name, last_name, designation, department, location, phone_number }
 * Updates one user's profile details. An admin may edit any user, everyone else only themselves.
 * Role, status and team lead are changed by admin-only endpoints, not this one.
 * 200: the updated profile (same shape as the GET above).  403: not allowed.  422: validation error.
 */
export const updateUserProfile = async (userId, details) => {
    return api.patch(`/rfp/auth/users/${encodeURIComponent(userId)}`, details);
};

/**
 * POST /rfp/auth/users/{user_id}/avatar   (multipart/form-data, field `file`)
 * Uploads a profile picture: png, jpg or webp, up to 2 MB. The server centre-crops it to a square
 * and shrinks it to 256 px. Only the user themselves or an admin may do this.
 * 200: the updated profile (same shape as the GET above) with the new avatar_url.
 * 400: not a png, jpg or webp image, or larger than 2 MB.  403: not allowed.  422: validation error.
 */
export const uploadProfilePicture = async (userId, file) => {
    const formData = new FormData();
    formData.append('file', file);
    // The shared client drops its JSON Content-Type for FormData, so the browser adds the boundary.
    return api.post(`/rfp/auth/users/${encodeURIComponent(userId)}/avatar`, formData);
};

/**
 * DELETE /rfp/auth/users/{user_id}/avatar
 * Removes the profile picture. 200: the updated profile (avatar_url is empty now).
 * 422: validation error.
 */
export const removeProfilePicture = async (userId) => {
    return api.delete(`/rfp/auth/users/${encodeURIComponent(userId)}/avatar`);
};


/**
 * Change the logged-in user's password.
 * POST /rfp/auth/change-password  { old_password, new_password, confirm_password }
 * Needs the login token (added by the shared client) and a password that is already set.
 * 200: message string.
 * 400: old password is wrong, or the new one is the same as the old one.
 * 403: password not set yet (use set-password first).
 * 422: validation error.
 */
export const changePassword = async ({ currentPassword, newPassword, confirmPassword }) => {
    return api.post('/rfp/auth/change-password', {
        old_password: currentPassword,
        new_password: newPassword,
        confirm_password: confirmPassword,
    });
};